import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, Link, useLocation } from "react-router-dom";
import {
  MaintenanceProvider,
  MaintenanceGuard,
} from "./contexts/MaintenanceContext";
import { AuthProvider } from "./contexts/AuthContext";
import "./styles/MaintenanceMode.css";
import { captureFacebookAdParams } from "./utils/adTracking";
import { captureFirstTouch } from "./utils/sourceTracking";
import {
  FiFileText,
  FiImage,
  FiGlobe, // Icon for WebsitePostGenerator
  FiLogOut,
  FiMenu,
  FiSettings,
  FiSave,
  FiUser,
  FiPlusCircle, // Add new icon for product creation
  FiBox, // Add new icon for products
  FiChevronDown,
  FiChevronRight,
  FiAlertTriangle, // Add warning icon
  FiMessageCircle, // Thêm import cho icon mới
  FiAward, // Thêm import FiAward
  FiDollarSign, // Thêm icon cho Affiliate và giao dịch
  FiShield, // Thêm icon cho Admin
  FiUsers, // Thêm icon cho quản lý người dùng
  FiActivity, // Thêm icon cho quản lý lỗi
  FiClock, // Thêm icon cho lịch sử bài viết
  FiDatabase, // Thêm icon cho quản lý dữ liệu
  FiUserCheck, // Thêm icon cho bài viết người dùng
  FiSend, // Thêm icon cho gửi thông báo
  FiRss, // Thêm icon cho quản lý tin tức
  FiMessageSquare, // Icon for Feedback
  FiEdit3,
  FiList,
  FiHelpCircle, // Add help icon
  FiVideo, // Icon for Video Creator
  FiLock,
} from "react-icons/fi"; // Add new icons

import ErrorBoundary from "./components/ErrorBoundary"; // Import ErrorBoundary
import NoIndexWrapper from "./components/NoIndexWrapper"; // SEO: noindex for private routes
import NormalPostGenerator from "./NormalPostGenerator";
import PhotosPostGenerator from "./PhotosPostGenerator";
import WebsitePostGenerator from "./WebsitePostGenerator"; // Import WebsitePostGenerator
import ClassifiedAdGenerator from "./ClassifiedAdGenerator"; // Import ClassifiedAdGenerator
import LoginFB from "./LoginFB";
import { ref, onValue, get, set, update, getDatabase } from "firebase/database"; // Import Firebase database functions
import AutoPostGenerator from "./AutoPostGenerator";
import ProductAutoPostGenerator from "./ProductAutoPostGenerator";
import AutoPostConfigScreen from "./AutoPostConfigScreen";
import ManualPostGenerator from "./ManualPostGenerator";
import ManualPostConfigScreen from "./ManualPostConfigScreen";
import ProfileScreen from "./ProfileScreen";
import ProductCreateScreen from "./ProductCreateScreen"; // Import the new screen
import ProductsScreen from "./ProductsScreen"; // Import the new screen
import LandingPage1 from "./LandingPage1";
import PricingScreen from "./components/PricingScreen";
import AddListImagesScreen from "./AddListImagesScreen";
import ProductImagesManagerScreen from "./ProductImagesManagerScreen";
import { db } from "./firebase/config";
import PolicyScreen from "./PolicyScreen"; // Thêm dòng này ở phần import
import TermsScreen from "./TermsScreen"; // Thêm dòng này ở phần import
import NewsScreen from "./NewsScreen";
import { HelmetProvider } from "react-helmet-async";
import { Header as LandingHeader } from "./LandingPage1"; // Nhớ export Header từ LandingPage1
import NewsDetailScreen from "./NewsDetailScreen";
import AdminNewsGenerator from "./AdminNewsGenerator"; // Import AdminNewsGenerator
import MetaPixelPageTracker from "./components/MetaPixelPageTracker"; // Import Meta Pixel tracker
import { analytics } from "./firebase/config";
import { logEvent } from "firebase/analytics";
import AffiliateScreen from "./components/AffiliateScreen"; // Thêm import AffiliateScreen
import LanguageSelector from "./components/LanguageSelector"; // Import LanguageSelector thay vì GoogleTranslate
import AdminDashboard from "./AdminDashboard"; // Import AdminDashboard
import AdminUserManagement from "./AdminUserManagement"; // Import AdminUserManagement
import AdminUserDetailPage from "./AdminUserDetailPage"; // Import AdminUserDetailPage
import AdminAffiliateManagement from "./AdminAffiliateManagement";
import AdminWithdrawalsScreen from "./AdminWithdrawalsScreen";
import AdminErrorManagement from "./AdminErrorManagement"; // Import AdminErrorManagement
import AdminPostManagement from "./AdminPostManagement"; // Import AdminPostManagement
import AdminTransactionManagement from "./AdminTransactionManagement"; // Import AdminTransactionManagement
import AdminNotificationScreen from "./AdminNotificationScreen"; // Import AdminNotificationScreen
import AdminNewsManagement from "./AdminNewsManagement"; // Import AdminNewsManagement
import AdminAutoSettings from "./AdminAutoSettings"; // Import AdminAutoSettings
import AdminPostHistoryManagement from "./AdminPostHistoryManagement"; // Import AdminPostHistoryManagement
import AdminUserPostManagement from "./AdminUserPostManagement"; // Import AdminUserPostManagement
import AdminCommentManagement from "./AdminCommentManagement";
import NotificationScreen from "./NotificationScreen";
import NotificationBell from "./components/NotificationBell";

import FeedbackForm from "./components/FeedbackForm"; // Import FeedbackForm
import SystemUpgradePopup from "./components/SystemUpgradePopup"; // Import System Upgrade Popup
import NewUserTrialPopup from "./components/NewUserTrialPopup"; // Import NewUserTrialPopup
import { Outlet } from "react-router-dom"; // Import Outlet
import GuideLayout from "./pages/guide/GuideLayout";
import GuidePage from "./pages/guide/GuidePage";
import {
  startHereGuide,
  autoPostGuide,
  addListImagesGuide,
  productAutoPostGuide,
  manualPostGuide,
  normalPostGuide,
  photosPostGuide,
  websitePostGuide,
  classifiedAdGuide,
  manageAutomationsGuide,
  facebookCommentGuide,
  videoCreatorGuide,
  leadEngineGuide,
  affiliateGuide,
} from "./pages/guide/GuideContents";
import VideoCreatorScreen from "./VideoCreatorScreen"; // Import VideoCreatorScreen
import PartnerToolPage from "./pages/PartnerToolPage"; // Import PartnerToolPage
import FacebookCommentAutomationScreen from "./FacebookCommentAutomationScreen";
import FacebookCommentConfigListScreen from "./FacebookCommentConfigListScreen";
import LeadConversionDashboard from "./components/LeadConversionDashboard";
import {
  buildConnectedFanpageRecord,
  fetchGrantedFacebookScopes,
} from "./utils/facebookCommentPermissions";

// Layout component using MaintenanceGuard
const MaintenanceLayout = () => {
  return (
    <MaintenanceGuard>
      <Outlet />
    </MaintenanceGuard>
  );
};

const SidebarSectionIcon = ({ icon: Icon, className = "" }) => (
  <Icon
    className={`mr-2 h-4 w-4 flex-shrink-0 ${className}`}
    aria-hidden="true"
  />
);

const AppContent = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("userAccessToken"),
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // State to handle sidebar collapse
  const [points, setPoints] = useState(0); // State to store user points
  const [commentQuota, setCommentQuota] = useState(null); // State for comment quota
  const [showMembershipWarning, setShowMembershipWarning] = useState(
    sessionStorage.getItem("showMembershipWarning") === "true",
  );
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [showTrialPopup, setShowTrialPopup] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    content: true,
    automation: true,
    video: true, // Add video group
    settings: true,
  });
  const location = useLocation();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  let lastScrollY = window.scrollY;
  const isCommentFeatureLocked = Boolean(
    commentQuota && !commentQuota.hasAccess,
  );
  const commentFeatureRequirementText = "Yêu cầu gói Chuyên nghiệp trở lên";

  // Lắng nghe cờ showNewUserTrialPopup từ localStorage dành cho người dùng đăng ký mới
  useEffect(() => {
    if (isLoggedIn) {
      const shouldShow = localStorage.getItem("showNewUserTrialPopup");
      if (shouldShow === "true") {
        console.log("🎁 Người dùng mới đăng nhập thành công. Hiển thị Popup Đặc quyền Premium Trial!");
        setShowTrialPopup(true);
        localStorage.removeItem("showNewUserTrialPopup");
      }
    }
  }, [isLoggedIn]);

  // Thêm tracking cho page views và bắt tham số quảng cáo Facebook
  useEffect(() => {
    try {
      // Capture first-touch source attribution (chỉ lưu lần đầu)
      captureFirstTouch();

      // Bắt và lưu tham số quảng cáo Facebook (backward-compatible)
      captureFacebookAdParams();

      // Track page view
      if (analytics) {
        logEvent(analytics, "page_view", {
          page_path: location.pathname,
          page_title: document.title,
        });
      }
    } catch (error) {
      console.error("Analytics error:", error);
    }
  }, [location]);

  // Global handler: xử lý OAuth redirect hash token (cho connect fanpage flow)
  useEffect(() => {
    const hash = window.location.hash;
    const pendingAction = localStorage.getItem("fb_pending_action");

    // Chỉ xử lý khi có access_token trong hash VÀ có pending action từ ProfileScreen
    if (hash && hash.includes("access_token") && pendingAction) {
      console.log(
        "🔵 [App.js] Detected OAuth redirect with pending action:",
        pendingAction,
      );

      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get("access_token");
      const expiresIn = params.get("expires_in");

      if (accessToken) {
        // Xóa hash khỏi URL ngay lập tức
        window.history.replaceState(null, "", window.location.pathname);

        // Xử lý token async
        (async () => {
          try {
            // Exchange short-lived → long-lived token
            const res = await fetch(
              `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${process.env.REACT_APP_FB_CLIENT_ID}&client_secret=${process.env.REACT_APP_FB_CLIENT_SECRET}&fb_exchange_token=${accessToken}`,
            );
            const data = await res.json();
            const longLivedToken = data.access_token || accessToken;
            const tokenExpiresIn = data.expires_in || expiresIn || 5184000;
            const expiryTime = Date.now() + tokenExpiresIn * 1000;

            // Lưu token
            localStorage.setItem("facebookAccessToken", longLivedToken);
            localStorage.setItem("facebookAccessTokenExpiry", expiryTime);

            const userId = localStorage.getItem("userId");
            if (userId) {
              const database = getDatabase();
              const userTokenRef = ref(
                database,
                `users/${userId}/facebookAuth`,
              );
              const grantedScopes =
                await fetchGrantedFacebookScopes(longLivedToken);
              await update(userTokenRef, {
                userAccessToken: longLivedToken,
                userAccessTokenExpiry: expiryTime,
                tokenType: "long_lived_user_token",
                lastUpdated: Date.now(),
                grantedScopes,
              });

              // Fetch fanpages và lưu vào Firebase
              console.log("🔵 [App.js] Fetching fanpages with new token...");
              const fanpageRes = await fetch(
                `https://graph.facebook.com/v21.0/me/accounts?access_token=${longLivedToken}&fields=id,name,access_token,category&limit=100`,
              );
              const fanpageData = await fanpageRes.json();

              if (fanpageData.data && fanpageData.data.length > 0) {
                const connectedFanpagesRef = ref(
                  database,
                  `users/${userId}/connectedFanpages`,
                );
                const updatedFanpages = {};

                for (const page of fanpageData.data) {
                  updatedFanpages[page.id] = buildConnectedFanpageRecord(page, {
                    accessToken: page.access_token || null,
                    grantedScopes,
                    tokenStatus: page.access_token ? "active" : "error",
                  });
                }

                await set(connectedFanpagesRef, updatedFanpages);
                console.log(
                  `✅ [App.js] Saved ${fanpageData.data.length} fanpages to Firebase`,
                );
              }
            }

            // Redirect về trang gốc
            const redirectAfter =
              localStorage.getItem("fb_redirect_after") || "/auto-post";
            localStorage.removeItem("fb_pending_action");
            localStorage.removeItem("fb_redirect_after");
            console.log("✅ [App.js] Redirecting to:", redirectAfter);
            window.location.href = redirectAfter;
          } catch (error) {
            console.error(
              "❌ [App.js] Error processing OAuth redirect:",
              error,
            );
            localStorage.removeItem("fb_pending_action");
            localStorage.removeItem("fb_redirect_after");
          }
        })();
      }
    }
  }, []); // Chỉ chạy 1 lần khi mount

  useEffect(() => {
    const handleStorageChange = () => {
      setIsLoggedIn(!!localStorage.getItem("userAccessToken"));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const userId = localStorage.getItem("userId");

      // OPTIMIZATION: Tách listeners riêng cho từng field thay vì download toàn bộ user profile
      // Điều này giảm bandwidth đáng kể khi chỉ 1 field thay đổi

      // Listener cho points - chỉ download khi points thay đổi
      const pointsRef = ref(db, `users/${userId}/points`);
      const unsubscribePoints = onValue(pointsRef, (snapshot) => {
        const pointsData = snapshot.val();
        let calculatedPoints = 0;

        if (pointsData) {
          if (
            pointsData.points !== undefined &&
            pointsData.purchasedPoints !== undefined
          ) {
            calculatedPoints = pointsData.points + pointsData.purchasedPoints;
          } else if (typeof pointsData === "number") {
            calculatedPoints = pointsData;
          }
        }

        setPoints(calculatedPoints);
        // Lưu vào localStorage để membership listener có thể đọc
        localStorage.setItem("currentPoints", calculatedPoints.toString());

        // Cập nhật membership warning dựa trên points mới
        const storedMembership = localStorage.getItem("hasMembership");
        const hasMembership = storedMembership === "true";
        if (calculatedPoints > 0 && !hasMembership) {
          sessionStorage.setItem("showMembershipWarning", "true");
          setShowMembershipWarning(true);
        } else {
          sessionStorage.removeItem("showMembershipWarning");
          setShowMembershipWarning(false);
        }
      });

      // Listener cho fanpages - chỉ download khi fanpages thay đổi
      const fanpagesRef = ref(db, `users/${userId}/fanpages`);
      const unsubscribeFanpages = onValue(fanpagesRef, (snapshot) => {
        const fanpagesData = snapshot.val();
        if (fanpagesData) {
          localStorage.setItem("fanpages", JSON.stringify(fanpagesData));
        } else {
          localStorage.removeItem("fanpages");
        }
      });

      // Listener cho membership - chỉ download khi membership thay đổi
      const membershipRef = ref(db, `users/${userId}/membership`);
      const unsubscribeMembership = onValue(membershipRef, (snapshot) => {
        const membership = snapshot.val();
        // Một membership được coi là có hiệu lực nếu nó tồn tại và khác 0
        const hasMembership = membership !== undefined && membership !== null && Number(membership) !== 0;

        // Lưu vào localStorage để points listener có thể đọc
        localStorage.setItem("hasMembership", hasMembership.toString());
        localStorage.setItem("currentMembership", membership != null ? String(membership) : "");

        // Cập nhật membership warning dựa trên membership mới
        const storedPoints = localStorage.getItem("currentPoints");
        const currentPoints = storedPoints ? parseInt(storedPoints, 10) : 0;

        if (currentPoints > 0 && !hasMembership) {
          sessionStorage.setItem("showMembershipWarning", "true");
          setShowMembershipWarning(true);
        } else {
          sessionStorage.removeItem("showMembershipWarning");
          setShowMembershipWarning(false);
        }
      });

      // Listener cho membershipExpiry + isTrial — phát hiện gói trải nghiệm hết hạn
      const expiryRef = ref(db, `users/${userId}/membershipExpiry`);
      const isTrialRef = ref(db, `users/${userId}/isTrial`);

      const updateTrialExpiredState = () => {
        const storedExpiry = localStorage.getItem("membershipExpiry");
        const storedIsTrial = localStorage.getItem("isTrial");
        const storedMembership = localStorage.getItem("currentMembership");
        const expiry = storedExpiry ? parseInt(storedExpiry, 10) : 0;
        const isTrial = storedIsTrial === "true";
        const membership = storedMembership ? Number(storedMembership) : 0;
        // Gói trải nghiệm hết hạn: isTrial=true HOẶC membership=6, VÀ expiry đã qua
        const trialExpired = (isTrial || membership === 6) && expiry > 0 && Date.now() > expiry;
        setIsTrialExpired(trialExpired);
      };

      const unsubscribeExpiry = onValue(expiryRef, (snapshot) => {
        const expiry = snapshot.val();
        localStorage.setItem("membershipExpiry", expiry != null ? String(expiry) : "0");
        updateTrialExpiredState();
      });

      const unsubscribeIsTrial = onValue(isTrialRef, (snapshot) => {
        const isTrial = snapshot.val();
        localStorage.setItem("isTrial", isTrial ? "true" : "false");
        updateTrialExpiredState();
      });

      // Cleanup function - hủy tất cả listeners khi component unmount hoặc logout
      return () => {
        unsubscribePoints();
        unsubscribeFanpages();
        unsubscribeMembership();
        unsubscribeExpiry();
        unsubscribeIsTrial();
      };
    }
  }, [isLoggedIn]);

  const handleLogout = () => {
    // Track logout event
    try {
      if (analytics) {
        logEvent(analytics, "logout");
      }
    } catch (error) {
      console.error("Analytics error:", error);
    }
    localStorage.clear();
    sessionStorage.clear();
    setIsLoggedIn(false);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const responseFacebook = (response) => {
    if (response.status !== "connected") {
      window.FB.login(
        function (response) {
          if (response.authResponse) {
            // Track successful login
            try {
              if (analytics) {
                logEvent(analytics, "login", {
                  method: "facebook",
                });
              }
            } catch (error) {
              console.error("Analytics error:", error);
            }
            window.FB.api("/me", function (response) {});
          }
        },
        { scope: "public_profile,email" },
      );
    }
  };

  // First check if we're on the landing page, pricing page, or news page
  if (
    location.pathname === "/" ||
    location.pathname === "/landing-page" ||
    location.pathname === "/pricing" ||
    location.pathname === "/policy" ||
    location.pathname === "/terms" ||
    location.pathname === "/news" ||
    location.pathname.startsWith("/news/") ||
    location.pathname === "/guide" ||
    location.pathname.startsWith("/guide/") ||
    location.pathname.startsWith("/partner/") ||
    location.pathname === "/admin-news" ||
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/") ||
    location.pathname === "/portfolio"
  ) {
    if (location.pathname === "/portfolio") {
      window.location.href = "/portfolio/index.html";
      return null;
    }
    if (location.pathname === "/" || location.pathname === "/landing-page") {
      return <LandingPage1 />;
    }
    if (location.pathname === "/policy") {
      return <PolicyScreen />;
    }
    if (location.pathname === "/terms") {
      return <TermsScreen />;
    }
    if (location.pathname === "/admin-news") {
      return <AdminNewsGenerator />;
    }
    if (location.pathname === "/admin") {
      // Redirect to admin dashboard
      window.location.href = "/admin/dashboard";
      return null;
    }
    if (location.pathname === "/admin/dashboard") {
      return <AdminDashboard />;
    }
    if (location.pathname === "/admin/users") {
      return <AdminUserManagement setActiveMenu={null} />;
    }
    if (location.pathname.startsWith("/admin/users/")) {
      return (
        <Routes>
          <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
        </Routes>
      );
    }
    if (location.pathname === "/admin/affiliates") {
      return <AdminAffiliateManagement />;
    }
    if (location.pathname === "/admin/withdrawals") {
      return <AdminWithdrawalsScreen />;
    }
    if (location.pathname === "/admin/posts") {
      return <AdminPostManagement />;
    }
    if (location.pathname === "/admin/user-posts") {
      return <AdminUserPostManagement />;
    }
    if (location.pathname === "/admin/post-history") {
      return <AdminPostHistoryManagement />;
    }
    if (location.pathname === "/admin/transactions") {
      return <AdminTransactionManagement />;
    }
    if (location.pathname === "/admin/errors") {
      return <AdminErrorManagement />;
    }
    if (location.pathname === "/admin/notifications") {
      return <AdminNotificationScreen />;
    }
    if (location.pathname === "/admin/news") {
      return <AdminNewsManagement />;
    }
    if (
      location.pathname === "/admin/lead-engine" ||
      location.pathname.startsWith("/admin/lead-engine/")
    ) {
      return <AdminCommentManagement routeMode />;
    }
    if (location.pathname === "/admin/settings") {
      return <AdminDashboard />;
    }
    if (location.pathname === "/admin/auto-settings") {
      return <AdminAutoSettings />;
    }
    if (location.pathname === "/news" || location.pathname.includes("/news/")) {
      return (
        <>
          <LandingHeader />
          <Routes>
            <Route path="/news" element={<NewsScreen />} />
            <Route path="/news/:slug" element={<NewsDetailScreen />} />
          </Routes>
        </>
      );
    }
    if (location.pathname.startsWith("/partner/")) {
      return (
        <Routes>
          <Route path="/partner/:toolSlug" element={<PartnerToolPage />} />
        </Routes>
      );
    }
    if (
      location.pathname === "/guide" ||
      location.pathname.startsWith("/guide/")
    ) {
      return (
        <>
          <LandingHeader />
          <Routes>
            <Route path="/guide" element={<GuideLayout />}>
              <Route
                index
                element={<Navigate to="/guide/start-here" replace />}
              />
              <Route
                path="start-here"
                element={<GuidePage {...startHereGuide} />}
              />
              <Route
                path="auto-post"
                element={<GuidePage {...autoPostGuide} />}
              />
              <Route
                path="add-list-images"
                element={<GuidePage {...addListImagesGuide} />}
              />
              <Route
                path="product-auto-post"
                element={<GuidePage {...productAutoPostGuide} />}
              />
              <Route
                path="manual-post"
                element={<GuidePage {...manualPostGuide} />}
              />
              <Route
                path="normal-post"
                element={<GuidePage {...normalPostGuide} />}
              />
              <Route
                path="photos-post"
                element={<GuidePage {...photosPostGuide} />}
              />
              <Route
                path="website-post"
                element={<GuidePage {...websitePostGuide} />}
              />
              <Route
                path="classified-ad"
                element={<GuidePage {...classifiedAdGuide} />}
              />
              <Route
                path="manage-automations"
                element={<GuidePage {...manageAutomationsGuide} />}
              />
              <Route
                path="facebook-comment-automation"
                element={<GuidePage {...facebookCommentGuide} />}
              />
              <Route
                path="video-creator"
                element={<GuidePage {...videoCreatorGuide} />}
              />
              <Route
                path="lead-engine"
                element={<GuidePage {...leadEngineGuide} />}
              />
              <Route
                path="affiliate"
                element={<GuidePage {...affiliateGuide} />}
              />
            </Route>
          </Routes>
        </>
      );
    }
    return <PricingScreen />;
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {" "}
      {/* Added min-h-screen */}
      {isLoggedIn ? (
        <>
          {/* Overlay for mobile */}
          {isSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Mobile Header */}
          <header className="md:hidden fixed top-0 left-0 right-0 h-12 bg-gray-700 shadow-md z-30 flex items-center px-4">
            <button
              className="text-white mr-4"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <FiMenu className="text-xl" /> {/* Reduced icon size */}
            </button>
            <div className="flex-1 flex justify-center items-center">
              <img
                src={require("./images/logo1.png")}
                alt="Logo"
                className="h-6 w-auto object-contain"
              />
            </div>
            <div className="ml-auto">
              <NotificationBell collapsed />
            </div>
          </header>

          {/* Sidebar Navigation */}
          <nav
            className={`bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col fixed md:sticky top-0 h-screen z-20 transition-all duration-300 ease-in-out shadow-2xl ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0 md:static md:mt-0 mt-12 ${
              isSidebarCollapsed ? "w-20" : "w-80"
            } border-r border-slate-700/50`}
          >
            {/* Container cho toàn bộ nội dung có thể scroll */}
            <div className="flex flex-col h-full">
              {/* Phần có thể scroll */}
              <div className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
                {/* Logo và header section */}
                <div className="flex-shrink-0">
                  <div className={`hidden md:flex items-center mb-6 ${isSidebarCollapsed ? "flex-col gap-4" : "justify-between"}`}>
                    <button
                      className={`text-white p-2 hover:bg-slate-700/50 rounded-lg transition-all duration-200 ${
                        isSidebarCollapsed ? "mx-auto" : ""
                      }`}
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    >
                      <FiMenu className="text-xl" />
                    </button>
                    <NotificationBell collapsed={isSidebarCollapsed} />
                  </div>
                  {!isSidebarCollapsed && (
                    <div className="text-center mb-8">
                      <img
                        src={require("./images/logo1.webp")}
                        alt="Logo"
                        className={`hidden md:block mb-4 mx-auto rounded-xl shadow-lg object-contain w-100 transition-all duration-300`}
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Points Section với design mới */}
                  <div
                    className={`mb-6 ${isSidebarCollapsed ? "px-1" : "px-4"}`}
                  >
                    <div
                      className={`bg-gradient-to-r from-slate-800/80 to-slate-700/80 backdrop-blur-sm rounded-2xl ${
                        isSidebarCollapsed ? "p-2" : "p-4"
                      } border shadow-lg ${
                        isTrialExpired
                          ? "border-red-400/60 bg-gradient-to-r from-red-900/30 to-orange-900/20 animate-pulse"
                          : points < 10
                            ? "border-red-400/50 bg-gradient-to-r from-red-900/20 to-red-800/20"
                            : points < 100
                              ? "border-orange-400/50 bg-gradient-to-r from-orange-900/20 to-orange-800/20"
                              : "border-green-400/50 bg-gradient-to-r from-green-900/20 to-green-800/20"
                      }`}
                    >
                      <div className="text-center">
                        {isSidebarCollapsed ? (
                          <Link to="/pricing" className="flex flex-col items-center group" title={isTrialExpired ? "Gói Trải nghiệm đã hết hạn — Nâng cấp ngay" : undefined}>
                            <div
                              className={`text-sm font-bold ${
                                isTrialExpired
                                  ? "text-red-400"
                                  : points < 10
                                    ? "text-red-400"
                                    : points < 100
                                      ? "text-orange-400"
                                      : "text-green-400"
                              }`}
                            >
                              {points > 999
                                ? `${Math.floor(points / 1000)}k`
                                : points}
                            </div>
                            {isTrialExpired && (
                              <FiAlertTriangle className="text-red-400 mt-1 h-3.5 w-3.5 animate-pulse" />
                            )}
                          </Link>
                        ) : (
                          <>
                            <div className="flex items-center justify-center mb-1">
                              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 mr-2"></div>
                              <span className="text-slate-300 font-medium">
                                Tổng điểm
                              </span>
                            </div>
                            <div
                              className={`text-3xl font-bold ${
                                points < 10
                                  ? "text-red-400"
                                  : points < 100
                                    ? "text-orange-400"
                                    : "text-green-400"
                              }`}
                            >
                              {points.toLocaleString()}
                            </div>
                          </>
                        )}

                        {!isSidebarCollapsed && (
                          <>
                            {isTrialExpired ? (
                              <>
                                <div className="mt-2 mb-2 px-3 py-2 rounded-lg bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-400/40">
                                  <div className="flex items-start text-red-300 text-sm mb-1">
                                    <FiAlertTriangle
                                      className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-red-400 animate-pulse"
                                      aria-hidden="true"
                                    />
                                    <span className="font-semibold text-red-200">
                                      Gói Trải nghiệm đã hết hạn
                                    </span>
                                  </div>
                                  <p className="text-red-200/80 text-xs leading-relaxed ml-6">
                                    Nâng cấp để tiếp tục tạo bài viết AI, đăng bài tự động & nhiều tính năng premium
                                  </p>
                                </div>
                                <Link
                                  to="/pricing"
                                  state={{ fromMenu: true }}
                                  className="inline-flex items-center justify-center w-full px-4 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 animate-pulse"
                                >
                                  🚀 Nâng cấp ngay
                                </Link>
                              </>
                            ) : showMembershipWarning ? (
                              <>
                                <div className="mb-3 flex items-start text-amber-300 text-sm">
                                  <FiAlertTriangle
                                    className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0"
                                    aria-hidden="true"
                                  />
                                  <span>
                                    Vui lòng nâng cấp gói thành viên để sử dụng
                                    dịch vụ
                                  </span>
                                </div>
                                <Link
                                  to="/pricing"
                                  state={{ fromMenu: true }}
                                  className="inline-block w-full px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                  Nâng cấp ngay
                                </Link>
                              </>
                            ) : points >= 10 && points < 100 ? (
                              <>
                                <div className="text-orange-300 mb-2 text-sm flex items-start">
                                  <FiAlertTriangle
                                    className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0"
                                    aria-hidden="true"
                                  />
                                  <span>Số điểm của bạn đang thấp!</span>
                                </div>
                                <div className="text-orange-200 mb-3 text-xs">
                                  Nạp thêm điểm để tránh ảnh hưởng đến chức năng
                                  đăng bài tự động
                                </div>
                                <Link
                                  to="/pricing"
                                  state={{ fromMenu: true }}
                                  className="inline-block w-full px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                  Nạp điểm ngay
                                </Link>
                              </>
                            ) : (
                              points < 10 && (
                                <>
                                  <div className="text-red-300 mb-2 text-sm flex items-start">
                                    <FiAlertTriangle
                                      className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0"
                                      aria-hidden="true"
                                    />
                                    <span>
                                      ĐỪNG BỎ LỠ NHỮNG TÍNH NĂNG HẤP DẪN!
                                    </span>
                                  </div>
                                  <div className="text-red-200 mb-3 text-xs">
                                    Nạp điểm ngay để tiếp tục trải nghiệm
                                  </div>
                                  <Link
                                    to="/pricing"
                                    state={{ fromMenu: true }}
                                    className="inline-block w-full px-4 py-2.5 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                                  >
                                    Xem các gói nạp điểm
                                  </Link>
                                </>
                              )
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Comment Usage Section */}
                  {commentQuota && false && (
                    <div
                      className={`mb-6 ${isSidebarCollapsed ? "px-1" : "px-4"}`}
                    >
                      <div
                        className={`bg-gradient-to-r from-slate-800/80 to-slate-700/80 backdrop-blur-sm rounded-2xl ${
                          isSidebarCollapsed ? "p-2" : "p-4"
                        } border shadow-lg ${
                          !commentQuota.hasAccess
                            ? "border-slate-500/30"
                            : commentQuota.unlimited
                              ? "border-cyan-400/50 bg-gradient-to-r from-cyan-900/20 to-teal-900/20"
                              : commentQuota.remaining <= 0
                                ? "border-red-400/50 bg-gradient-to-r from-red-900/20 to-red-800/20"
                                : commentQuota.used >= commentQuota.quota * 0.8
                                  ? "border-orange-400/50 bg-gradient-to-r from-orange-900/20 to-orange-800/20"
                                  : "border-teal-400/30"
                        }`}
                      >
                        <div className="text-center">
                          {isSidebarCollapsed ? (
                            <div className="flex flex-col items-center">
                              <FiMessageCircle
                                className="h-3 w-3"
                                aria-hidden="true"
                              />
                              <div
                                className={`text-xs font-bold ${
                                  !commentQuota.hasAccess
                                    ? "text-slate-500"
                                    : commentQuota.unlimited
                                      ? "text-cyan-400"
                                      : commentQuota.remaining <= 0
                                        ? "text-red-400"
                                        : commentQuota.used >=
                                            commentQuota.quota * 0.8
                                          ? "text-orange-400"
                                          : "text-teal-400"
                                }`}
                              >
                                {!commentQuota.hasAccess
                                  ? "—"
                                  : commentQuota.unlimited
                                    ? "∞"
                                    : `${commentQuota.used}`}
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center justify-center mb-2">
                                <FiMessageCircle
                                  className="mr-2 h-4 w-4"
                                  aria-hidden="true"
                                />
                                <span className="text-slate-300 font-medium text-sm">
                                  Công suất engine
                                </span>
                              </div>

                              {!commentQuota.hasAccess ? (
                                <>
                                  <div className="mb-3 text-center">
                                    <div className="text-amber-200 text-xs font-semibold">
                                      Lead Conversion Engine mở từ gói Chuyên
                                      nghiệp+
                                    </div>
                                    <div className="text-slate-400 text-[11px] mt-1 leading-relaxed">
                                      Gói hiện tại chưa hỗ trợ engine xử lý lead
                                      từ comment.
                                    </div>
                                  </div>
                                  <Link
                                    to="/pricing"
                                    state={{ fromMenu: true }}
                                    className="inline-block w-full px-3 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-xs"
                                  >
                                    Xem gói hỗ trợ
                                  </Link>
                                </>
                              ) : commentQuota.unlimited ? (
                                <>
                                  <div className="text-2xl font-bold text-cyan-400 mb-1">
                                    Unlimited
                                  </div>
                                  <div className="text-cyan-300/70 text-xs">
                                    Đã dùng:{" "}
                                    {commentQuota.used.toLocaleString()} lượt xử
                                    lý
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* Số liệu */}
                                  <div
                                    className={`text-2xl font-bold mb-1 ${
                                      commentQuota.remaining <= 0
                                        ? "text-red-400"
                                        : commentQuota.used >=
                                            commentQuota.quota * 0.8
                                          ? "text-orange-400"
                                          : "text-teal-400"
                                    }`}
                                  >
                                    {commentQuota.used.toLocaleString()}
                                    <span className="text-base text-slate-400 font-normal">
                                      {" "}
                                      /{" "}
                                      {(
                                        commentQuota.quota +
                                        (commentQuota.purchasedQuota || 0)
                                      ).toLocaleString()}
                                    </span>
                                  </div>

                                  {/* Progress bar */}
                                  <div className="w-full bg-slate-600/50 rounded-full h-2 mb-2 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${
                                        commentQuota.remaining <= 0
                                          ? "bg-gradient-to-r from-red-500 to-red-400"
                                          : commentQuota.used >=
                                              commentQuota.quota * 0.8
                                            ? "bg-gradient-to-r from-orange-500 to-amber-400"
                                            : "bg-gradient-to-r from-teal-500 to-cyan-400"
                                      }`}
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          (commentQuota.used /
                                            (commentQuota.quota +
                                              (commentQuota.purchasedQuota ||
                                                0))) *
                                            100,
                                        )}%`,
                                      }}
                                    />
                                  </div>

                                  {/* Trạng thái */}
                                  {commentQuota.remaining <= 0 ? (
                                    <>
                                      <div className="text-red-300 text-xs mb-2 flex items-center justify-center">
                                        <FiAlertTriangle
                                          className="mr-1.5 h-3.5 w-3.5"
                                          aria-hidden="true"
                                        />
                                        <span>Đã hết quota tháng này</span>
                                      </div>
                                      <Link
                                        to="/pricing"
                                        state={{ fromMenu: true }}
                                        className="inline-block w-full px-3 py-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-medium rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-xs"
                                      >
                                        Mua thêm lượt
                                      </Link>
                                    </>
                                  ) : commentQuota.used >=
                                    commentQuota.quota * 0.8 ? (
                                    <div className="text-orange-300/80 text-xs flex items-center justify-center">
                                      <FiAlertTriangle
                                        className="mr-1.5 h-3.5 w-3.5"
                                        aria-hidden="true"
                                      />
                                      <span>
                                        Còn{" "}
                                        {commentQuota.remaining.toLocaleString()}{" "}
                                        lượt
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="text-teal-300/70 text-xs">
                                      Còn lại:{" "}
                                      {commentQuota.remaining.toLocaleString()}{" "}
                                      lượt
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Menu items với design mới */}
                <div className="flex-1">
                  <ul
                    className={`flex flex-col ${
                      isSidebarCollapsed ? "space-y-2" : "space-y-3"
                    } mb-8 md:mb-0`}
                  >
                    {/* Automation Group */}
                    <div className="pb-3">
                      {isSidebarCollapsed ? (
                        // Collapsed view - hiển thị tất cả các menu item con với tooltip
                        <div className="flex flex-col space-y-1">
                          {/* Tự động đăng bài từ chủ đề & website */}
                          <Link
                            to="/auto-post"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/auto-post"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Thiết lập lịch đăng"
                          >
                            <FiSettings className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/autopost-configs"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/autopost-configs"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Quản lý lịch đăng"
                          >
                            <FiSave className="text-lg text-slate-300" />
                          </Link>
                          {/* Tự động đăng bài từ ảnh/video */}
                          <Link
                            to="/add-photo-list"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/add-photo-list"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Thiết lập lịch đăng"
                          >
                            <FiImage className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/product-images-manager"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/product-images-manager"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Quản lý danh sách ảnh/video"
                          >
                            <FiBox className="text-lg text-slate-300" />
                          </Link>
                          {/* Tự động đăng bài từ sản phẩm */}
                          <Link
                            to="/product-auto-post"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/product-auto-post"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Thiết lập lịch đăng"
                          >
                            <FiBox className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/product-create"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/product-create"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Thêm sản phẩm mới"
                          >
                            <FiPlusCircle className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/products"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/products"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Danh sách sản phẩm"
                          >
                            <FiBox className="text-lg text-slate-300" />
                          </Link>
                          {/* Tự động đăng bài từ bài viết có sẵn */}
                          <Link
                            to="/manual-post"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/manual-post"
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Thiết lập lịch đăng"
                          >
                            <FiEdit3 className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/manual-post/configs"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname.startsWith(
                                "/manual-post/configs",
                              )
                                ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Quản lý lịch đăng"
                          >
                            <FiList className="text-lg text-slate-300" />
                          </Link>
                          {/* Lead Conversion Engine — sidebar collapsed icons (ẩn tạm)
                          <Link
                            to="/lead-dashboard"
                            className={`relative w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/lead-dashboard"
                                ? "bg-gradient-to-r from-teal-600/80 to-cyan-600/80 shadow-lg"
                                : isCommentFeatureLocked
                                ? "bg-amber-500/10 border border-amber-400/20 hover:bg-amber-500/15"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title={
                              isCommentFeatureLocked
                                ? `Dashboard lead • ${commentFeatureRequirementText}`
                                : "Dashboard lead"
                            }
                          >
                            <FiActivity
                              className={`text-lg ${
                                isCommentFeatureLocked &&
                                location.pathname !== "/lead-dashboard"
                                  ? "text-amber-200"
                                  : "text-slate-300"
                              }`}
                            />
                            {isCommentFeatureLocked &&
                              location.pathname !== "/lead-dashboard" && (
                                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_0_2px_rgba(15,23,42,0.95)]" />
                              )}
                          </Link>
                          <Link
                            to="/facebook-comment-automation"
                            className={`relative w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/facebook-comment-automation"
                                ? "bg-gradient-to-r from-teal-600/80 to-cyan-600/80 shadow-lg"
                                : isCommentFeatureLocked
                                ? "bg-amber-500/10 border border-amber-400/20 hover:bg-amber-500/15"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title={
                              isCommentFeatureLocked
                                ? `Cài đặt engine • ${commentFeatureRequirementText}`
                                : "Cài đặt engine"
                            }
                          >
                            <FiSettings
                              className={`text-lg ${
                                isCommentFeatureLocked &&
                                location.pathname !== "/facebook-comment-automation"
                                  ? "text-amber-200"
                                  : "text-slate-300"
                              }`}
                            />
                            {isCommentFeatureLocked &&
                              location.pathname !== "/facebook-comment-automation" && (
                                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_0_2px_rgba(15,23,42,0.95)]" />
                              )}
                          </Link>
                          <Link
                            to="/facebook-comment-automation/configs"
                            className={`relative w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/facebook-comment-automation/configs"
                                ? "bg-gradient-to-r from-teal-600/80 to-cyan-600/80 shadow-lg"
                                : isCommentFeatureLocked
                                ? "bg-amber-500/10 border border-amber-400/20 hover:bg-amber-500/15"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title={
                              isCommentFeatureLocked
                                ? `Quản lý config • ${commentFeatureRequirementText}`
                                : "Quản lý config"
                            }
                          >
                            <FiList
                              className={`text-lg ${
                                isCommentFeatureLocked &&
                                location.pathname !==
                                  "/facebook-comment-automation/configs"
                                  ? "text-amber-200"
                                  : "text-slate-300"
                              }`}
                            />
                            {isCommentFeatureLocked &&
                              location.pathname !==
                                "/facebook-comment-automation/configs" && (
                                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_0_2px_rgba(15,23,42,0.95)]" />
                              )}
                          </Link>
                          */}
                        </div>
                      ) : (
                        // Expanded view - hiển thị đầy đủ
                        <button
                          onClick={() => toggleGroup("automation")}
                          className="w-full text-white rounded-xl flex items-center justify-between p-4 transition-all duration-200 hover:bg-slate-700/50 group"
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-8 bg-gradient-to-b from-purple-400 to-pink-400 rounded-full mr-3 opacity-80"></div>
                            <span className="font-semibold text-slate-200 group-hover:text-white">
                              Đăng bài tự động với AI
                            </span>
                          </div>
                          {expandedGroups.automation ? (
                            <FiChevronDown className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          ) : (
                            <FiChevronRight className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          )}
                        </button>
                      )}
                      {!isSidebarCollapsed && expandedGroups.automation && (
                        <div className="ml-6 mt-3 space-y-2">
                          {/* Tự động đăng bài từ chủ đề & website */}
                          <div className="py-2">
                            <div className="text-purple-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <SidebarSectionIcon icon={FiFileText} />
                              <span>Tự động đăng bài từ chủ đề & website</span>
                            </div>
                            <div className="ml-4 space-y-2">
                              <li>
                                <Link
                                  to="/auto-post"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/auto-post"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiSettings
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/auto-post"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/auto-post"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Thiết lập lịch đăng
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/autopost-configs"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/autopost-configs"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiSave
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/autopost-configs"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/autopost-configs"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Quản lý lịch đăng
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>

                          {/* Tự động đăng bài từ ảnh/video */}
                          <div className="py-2 border-t border-slate-700/50">
                            <div className="text-purple-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <SidebarSectionIcon icon={FiImage} />
                              <span>Tự động đăng bài từ ảnh/video</span>
                            </div>
                            <div className="ml-4 space-y-2">
                              <li>
                                <Link
                                  to="/add-photo-list"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/add-photo-list"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiImage
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/add-photo-list"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/add-photo-list"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Thiết lập lịch đăng
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/product-images-manager"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname ===
                                    "/product-images-manager"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiBox
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname ===
                                      "/product-images-manager"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname ===
                                      "/product-images-manager"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Quản lý danh sách ảnh/video
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>

                          {/* Tự động đăng bài từ sản phẩm */}
                          <div className="py-2 border-t border-slate-700/50">
                            <div className="text-purple-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <SidebarSectionIcon icon={FiBox} />
                              <span>Tự động đăng bài từ sản phẩm</span>
                            </div>
                            <div className="ml-4 space-y-2">
                              <li>
                                <Link
                                  to="/product-auto-post"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/product-auto-post"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiBox
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/product-auto-post"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/product-auto-post"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Thiết lập lịch đăng
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/product-create"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/product-create"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiPlusCircle
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/product-create"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/product-create"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Thêm sản phẩm mới
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/products"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/products"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiBox
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/products"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/products"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Danh sách sản phẩm
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>

                          {/* Tự động đăng bài từ bài viết có sẵn */}
                          <div className="py-2 border-t border-slate-700/50">
                            <div className="text-emerald-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <SidebarSectionIcon icon={FiEdit3} />
                              <span>Tự động đăng bài từ bài viết có sẵn</span>
                            </div>
                            <div className="ml-4 space-y-2">
                              <li>
                                <Link
                                  to="/manual-post"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/manual-post"
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiEdit3
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/manual-post"
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/manual-post"
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Thiết lập lịch đăng
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/manual-post/configs"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname.startsWith(
                                      "/manual-post/configs",
                                    )
                                      ? "bg-gradient-to-r from-purple-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiList
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname.startsWith(
                                        "/manual-post/configs",
                                      )
                                        ? "text-purple-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname.startsWith(
                                        "/manual-post/configs",
                                      )
                                        ? "text-purple-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Quản lý lịch đăng
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>

                          {/* Lead Conversion Engine — nhóm sidebar (ẩn tạm)
                          <div className="py-2 border-t border-slate-700/50">
                            <div className="text-teal-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <span className="text-lg mr-2">💬</span>
                              <span>Lead Conversion Engine</span>
                              {isCommentFeatureLocked ? (
                                <span className="ml-2 px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-amber-400/15 text-amber-100 border border-amber-300/20">
                                  PRO+
                                </span>
                              ) : (
                                <span className="hot-badge-animated ml-2 px-2 py-0.5 text-white text-xs rounded-full font-bold">
                                  HOT
                                </span>
                              )}
                            </div>
                            <div className="ml-4 space-y-2">
                              {isCommentFeatureLocked && (
                                <div className="mx-3 mb-2 flex items-center justify-between gap-3 rounded-xl border border-amber-400/15 bg-slate-800/50 px-3 py-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <FiLock className="shrink-0 text-sm text-amber-200" />
                                    <span className="truncate text-xs font-medium text-slate-300">
                                      Cần gói Chuyên nghiệp+
                                    </span>
                                  </div>
                                  <Link
                                    to="/pricing"
                                    state={{ fromMenu: true }}
                                    className="shrink-0 text-xs font-semibold text-amber-100 transition hover:text-white"
                                    onClick={() => setIsSidebarOpen(false)}
                                  >
                                    Xem gói
                                  </Link>
                                </div>
                              )}
                              <li>
                                <Link
                                  to="/lead-dashboard"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/lead-dashboard"
                                      ? "bg-gradient-to-r from-teal-600/80 to-cyan-600/80 shadow-lg"
                                      : isCommentFeatureLocked
                                      ? "bg-slate-800/50 border border-amber-400/15 hover:bg-slate-800/70"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiActivity
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/lead-dashboard"
                                        ? "text-teal-200"
                                        : isCommentFeatureLocked
                                        ? "text-amber-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/lead-dashboard"
                                        ? "text-teal-100"
                                        : isCommentFeatureLocked
                                        ? "text-amber-50"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Dashboard lead
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/facebook-comment-automation"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/facebook-comment-automation"
                                      ? "bg-gradient-to-r from-teal-600/80 to-cyan-600/80 shadow-lg"
                                      : isCommentFeatureLocked
                                      ? "bg-slate-800/50 border border-amber-400/15 hover:bg-slate-800/70"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiSettings
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/facebook-comment-automation"
                                        ? "text-teal-200"
                                        : isCommentFeatureLocked
                                        ? "text-amber-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/facebook-comment-automation"
                                        ? "text-teal-100"
                                        : isCommentFeatureLocked
                                        ? "text-amber-50"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Cài đặt engine
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/facebook-comment-automation/configs"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname ===
                                    "/facebook-comment-automation/configs"
                                      ? "bg-gradient-to-r from-teal-600/80 to-cyan-600/80 shadow-lg"
                                      : isCommentFeatureLocked
                                      ? "bg-slate-800/50 border border-amber-400/15 hover:bg-slate-800/70"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiList
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname ===
                                      "/facebook-comment-automation/configs"
                                        ? "text-teal-200"
                                        : isCommentFeatureLocked
                                        ? "text-amber-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname ===
                                      "/facebook-comment-automation/configs"
                                        ? "text-teal-100"
                                        : isCommentFeatureLocked
                                        ? "text-amber-50"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Quản lý config
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>
                          */}
                        </div>
                      )}
                    </div>

                    {/* Video Creator Group */}
                    <div className="py-3 border-t border-slate-700/50">
                      {isSidebarCollapsed ? (
                        // Collapsed view
                        <div className="flex flex-col space-y-1">
                          <Link
                            to="/video-creator"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/video-creator"
                                ? "bg-gradient-to-r from-orange-600/80 to-pink-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Tạo Video AI"
                          >
                            <FiVideo className="text-lg text-slate-300" />
                          </Link>
                        </div>
                      ) : (
                        // Expanded view
                        <button
                          onClick={() => toggleGroup("video")}
                          className="w-full text-white rounded-xl flex items-center justify-between p-4 transition-all duration-200 hover:bg-slate-700/50 group"
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-8 bg-gradient-to-b from-orange-400 to-pink-400 rounded-full mr-3 opacity-80"></div>
                            <span className="font-semibold text-slate-200 group-hover:text-white">
                              Tạo Video với AI
                            </span>
                          </div>
                          {expandedGroups.video ? (
                            <FiChevronDown className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          ) : (
                            <FiChevronRight className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          )}
                        </button>
                      )}
                      {!isSidebarCollapsed && expandedGroups.video && (
                        <div className="ml-6 mt-3 space-y-2">
                          <div className="py-2">
                            <div className="text-orange-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <SidebarSectionIcon icon={FiVideo} />
                              <span>Tạo video quảng cáo</span>
                            </div>
                            <div className="ml-4 space-y-2">
                              <li>
                                <Link
                                  to="/video-creator"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/video-creator"
                                      ? "bg-gradient-to-r from-orange-600/80 to-pink-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiVideo
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/video-creator"
                                        ? "text-orange-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/video-creator"
                                        ? "text-orange-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Video từ ảnh của bạn
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content Management Group */}
                    <div className="py-3 border-t border-slate-700/50">
                      {isSidebarCollapsed ? (
                        // Collapsed view - hiển thị tất cả các menu item con với tooltip
                        <div className="flex flex-col space-y-1">
                          {/* Tạo bài viết với AI */}
                          <Link
                            to="/normal-post"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/normal-post"
                                ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Từ yêu cầu"
                          >
                            <FiFileText className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/photos-post"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/photos-post"
                                ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Từ hình ảnh/video"
                          >
                            <FiImage className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/website-post"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/website-post"
                                ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Đăng lên Website"
                          >
                            <FiGlobe className="text-lg text-slate-300" />
                          </Link>
                        </div>
                      ) : (
                        // Expanded view - hiển thị đầy đủ
                        <button
                          onClick={() => toggleGroup("content")}
                          className="w-full text-white rounded-xl flex items-center justify-between p-4 transition-all duration-200 hover:bg-slate-700/50 group"
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-8 bg-gradient-to-b from-blue-400 to-purple-400 rounded-full mr-3 opacity-80"></div>
                            <span className="font-semibold text-slate-200 group-hover:text-white">
                              Tạo bài viết
                            </span>
                          </div>
                          {expandedGroups.content ? (
                            <FiChevronDown className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          ) : (
                            <FiChevronRight className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          )}
                        </button>
                      )}
                      {!isSidebarCollapsed && expandedGroups.content && (
                        <div className="ml-6 mt-3 space-y-2">
                          {/* Manual Post Creation Sub-group */}
                          <div className="py-2">
                            <div className="text-blue-300 text-sm font-medium mb-3 px-3 flex items-center">
                              <SidebarSectionIcon icon={FiFileText} />
                              <span>Tạo bài viết với AI</span>
                            </div>
                            <div className="ml-4 space-y-2">
                              <li>
                                <Link
                                  to="/normal-post"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/normal-post"
                                      ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiFileText
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/normal-post"
                                        ? "text-blue-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/normal-post"
                                        ? "text-blue-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Từ chủ đề
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/photos-post"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/photos-post"
                                      ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiImage
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/photos-post"
                                        ? "text-blue-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/photos-post"
                                        ? "text-blue-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Từ hình ảnh/video
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/website-post"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/website-post"
                                      ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiGlobe
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/website-post"
                                        ? "text-blue-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/website-post"
                                        ? "text-blue-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Đăng lên Website
                                  </span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="/classified-ad"
                                  className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                    isSidebarCollapsed
                                      ? "justify-center px-2 py-3"
                                      : "justify-start p-3"
                                  } ${
                                    location.pathname === "/classified-ad"
                                      ? "bg-gradient-to-r from-blue-600/80 to-purple-600/80 shadow-lg"
                                      : "hover:bg-slate-700/50"
                                  }`}
                                  onClick={() => setIsSidebarOpen(false)}
                                >
                                  <FiFileText
                                    className={`text-xl ${
                                      !isSidebarCollapsed ? "mr-3" : ""
                                    } ${
                                      location.pathname === "/classified-ad"
                                        ? "text-blue-200"
                                        : "text-slate-400"
                                    }`}
                                  />
                                  <span
                                    className={`${
                                      isSidebarCollapsed ? "hidden" : ""
                                    } font-medium ${
                                      location.pathname === "/classified-ad"
                                        ? "text-blue-100"
                                        : "text-slate-300"
                                    }`}
                                  >
                                    Tin rao vặt
                                  </span>
                                </Link>
                              </li>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Settings Group */}
                    <div className="py-3 border-t border-slate-700/50">
                      {isSidebarCollapsed ? (
                        // Collapsed view - hiển thị tất cả các menu item con với tooltip
                        <div className="flex flex-col space-y-1">
                          <Link
                            to="/profile"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/profile"
                                ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Thiết lập thông tin"
                          >
                            <FiUser className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/pricing"
                            state={{ fromMenu: true }}
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/pricing"
                                ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Bảng giá"
                          >
                            <FiAward className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/affiliate"
                            className={`relative w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/affiliate"
                                ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Affiliate"
                          >
                            <FiDollarSign className="text-lg text-slate-300" />
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold px-1 rounded-full animate-pulse shadow-sm shadow-red-500/50 border border-red-400">HOT</span>
                          </Link>
                          <a
                            href="/landing-page#contact"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 hover:bg-slate-700/50"
                            onClick={() => setIsSidebarOpen(false)}
                            title="Hỗ trợ"
                          >
                            <FiMessageCircle className="text-lg text-slate-300" />
                          </a>
                          <Link
                            to="/guide"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname.startsWith("/guide")
                                ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Hướng dẫn sử dụng"
                          >
                            <FiHelpCircle className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/policy"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/policy"
                                ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Chính sách"
                          >
                            <FiFileText className="text-lg text-slate-300" />
                          </Link>
                          <Link
                            to="/terms"
                            className={`w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 ${
                              location.pathname === "/terms"
                                ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => setIsSidebarOpen(false)}
                            title="Điều khoản dịch vụ"
                          >
                            <FiFileText className="text-lg text-slate-300" />
                          </Link>
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsSidebarOpen(false);
                            }}
                            className="w-full rounded-lg flex items-center justify-center py-2 transition-all duration-200 transform hover:scale-105 hover:bg-red-600/30"
                            title="Đăng xuất"
                          >
                            <FiLogOut className="text-lg text-red-400" />
                          </button>
                        </div>
                      ) : (
                        // Expanded view - hiển thị đầy đủ
                        <button
                          onClick={() => toggleGroup("settings")}
                          className="w-full text-white rounded-xl flex items-center justify-between p-4 transition-all duration-200 hover:bg-slate-700/50 group"
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-8 bg-gradient-to-b from-amber-400 to-orange-400 rounded-full mr-3 opacity-80"></div>
                            <span className="font-semibold text-slate-200 group-hover:text-white">
                              Cài đặt
                            </span>
                          </div>
                          {expandedGroups.settings ? (
                            <FiChevronDown className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          ) : (
                            <FiChevronRight className="text-slate-400 group-hover:text-white transition-colors duration-200" />
                          )}
                        </button>
                      )}
                      {!isSidebarCollapsed && expandedGroups.settings && (
                        <div className="ml-6 mt-3 space-y-2">
                          <li>
                            <Link
                              to="/profile"
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } ${
                                location.pathname === "/profile"
                                  ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                  : "hover:bg-slate-700/50"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiUser
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } ${
                                  location.pathname === "/profile"
                                    ? "text-amber-200"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium ${
                                  location.pathname === "/profile"
                                    ? "text-amber-100"
                                    : "text-slate-300"
                                }`}
                              >
                                Thiết lập thông tin
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/pricing"
                              state={{ fromMenu: true }}
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } ${
                                location.pathname === "/pricing"
                                  ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                  : "hover:bg-slate-700/50"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiAward
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } ${
                                  location.pathname === "/pricing"
                                    ? "text-amber-200"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium ${
                                  location.pathname === "/pricing"
                                    ? "text-amber-100"
                                    : "text-slate-300"
                                }`}
                              >
                                Bảng giá
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/affiliate"
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 relative ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } ${
                                location.pathname === "/affiliate"
                                  ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                  : "hover:bg-slate-700/50"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiDollarSign
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } ${
                                  location.pathname === "/affiliate"
                                    ? "text-amber-200"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : "flex items-center"
                                } font-medium ${
                                  location.pathname === "/affiliate"
                                    ? "text-amber-100"
                                    : "text-slate-300"
                                }`}
                              >
                                Affiliate
                                <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md animate-pulse shadow-sm shadow-red-500/50 border border-red-400">HOT</span>
                              </span>
                            </Link>
                          </li>
                          <li>
                            <a
                              href="/landing-page#contact"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } hover:bg-slate-700/50`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiMessageCircle
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } text-slate-400`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium text-slate-300`}
                              >
                                Hỗ trợ
                              </span>
                            </a>
                          </li>
                          <li>
                            <Link
                              to="/guide"
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } ${
                                location.pathname.startsWith("/guide")
                                  ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                  : "hover:bg-slate-700/50"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiHelpCircle
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } ${
                                  location.pathname.startsWith("/guide")
                                    ? "text-amber-200"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium ${
                                  location.pathname.startsWith("/guide")
                                    ? "text-amber-100"
                                    : "text-slate-300"
                                }`}
                              >
                                Hướng dẫn sử dụng
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/policy"
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } ${
                                location.pathname === "/policy"
                                  ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                  : "hover:bg-slate-700/50"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiFileText
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } ${
                                  location.pathname === "/policy"
                                    ? "text-amber-200"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium ${
                                  location.pathname === "/policy"
                                    ? "text-amber-100"
                                    : "text-slate-300"
                                }`}
                              >
                                Chính sách
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/terms"
                              className={`text-white rounded-xl flex items-center transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } ${
                                location.pathname === "/terms"
                                  ? "bg-gradient-to-r from-amber-600/80 to-orange-600/80 shadow-lg"
                                  : "hover:bg-slate-700/50"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiFileText
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } ${
                                  location.pathname === "/terms"
                                    ? "text-amber-200"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium ${
                                  location.pathname === "/terms"
                                    ? "text-amber-100"
                                    : "text-slate-300"
                                }`}
                              >
                                Điều khoản dịch vụ
                              </span>
                            </Link>
                          </li>
                          <li>
                            <LanguageSelector
                              isSidebar={true}
                              isSidebarCollapsed={isSidebarCollapsed}
                            />
                          </li>
                          <li>
                            <button
                              onClick={() => {
                                handleLogout();
                                setIsSidebarOpen(false);
                              }}
                              className={`text-white rounded-xl flex items-center w-full transition-all duration-200 transform hover:scale-105 ${
                                isSidebarCollapsed
                                  ? "justify-center px-2 py-3"
                                  : "justify-start p-3"
                              } hover:bg-red-600/30 hover:border-red-500/50 border border-transparent`}
                            >
                              <FiLogOut
                                className={`text-xl ${
                                  !isSidebarCollapsed ? "mr-3" : ""
                                } text-red-400`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                } font-medium text-red-300`}
                              >
                                Đăng xuất
                              </span>
                            </button>
                          </li>
                        </div>
                      )}
                    </div>

                    {/* Feedback Button - Placed after Settings */}
                    <div className="py-3 border-t border-slate-700/50">
                      <button
                        onClick={() => setShowFeedbackForm(true)}
                        className={`text-white rounded-lg flex items-center w-full transition-all duration-200 transform hover:scale-105 hover:bg-indigo-600/30 ${
                          isSidebarCollapsed
                            ? "justify-center py-2"
                            : "justify-start p-3"
                        }`}
                        title={isSidebarCollapsed ? "Gửi Phản Hồi" : ""}
                      >
                        <FiMessageSquare
                          className={`${
                            isSidebarCollapsed ? "text-lg" : "text-xl"
                          } ${
                            !isSidebarCollapsed ? "mr-3" : ""
                          } text-indigo-400`}
                        />
                        {!isSidebarCollapsed && (
                          <span className="font-medium text-indigo-300">
                            Gửi Phản Hồi
                          </span>
                        )}
                      </button>
                    </div>
                  </ul>
                </div>
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <div className="flex-1 p-0 md:p-4 md:mt-0 mt-10 min-h-screen">
            {" "}
            {/* Added min-h-screen */}
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage1 />} />
              <Route path="/landing-page" element={<LandingPage1 />} />
              <Route
                path="/admin"
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route
                path="/admin/users"
                element={<AdminUserManagement setActiveMenu={null} />}
              />
              <Route
                path="/admin/users/:userId"
                element={<AdminUserDetailPage />}
              />
              <Route
                path="/admin/affiliates"
                element={<AdminAffiliateManagement />}
              />
              <Route
                path="/admin/withdrawals"
                element={<AdminWithdrawalsScreen />}
              />
              <Route path="/admin/posts" element={<AdminPostManagement />} />
              <Route
                path="/admin/user-posts"
                element={<AdminUserPostManagement />}
              />
              <Route
                path="/admin/post-history"
                element={<AdminPostHistoryManagement />}
              />
              <Route
                path="/admin/transactions"
                element={<AdminTransactionManagement />}
              />
              <Route path="/admin/errors" element={<AdminErrorManagement />} />
              <Route
                path="/admin/notifications"
                element={<AdminNotificationScreen />}
              />
              <Route path="/admin/news" element={<AdminNewsManagement />} />
              <Route path="/admin/news" element={<AdminNewsManagement />} />
              <Route
                path="/admin/lead-engine"
                element={<AdminCommentManagement routeMode />}
              />
              <Route
                path="/admin/lead-engine/customers"
                element={<AdminCommentManagement routeMode />}
              />
              <Route
                path="/admin/lead-engine/operations"
                element={<AdminCommentManagement routeMode />}
              />
              <Route
                path="/admin/lead-engine/analytics"
                element={<AdminCommentManagement routeMode />}
              />
              <Route path="/admin/settings" element={<AdminDashboard />} />
              <Route
                path="/admin/auto-settings"
                element={<AdminAutoSettings />}
              />

              {/* Protected routes wrapped in MaintenanceLayout */}
              <Route element={<MaintenanceLayout />}>
                <Route
                  path="/normal-post"
                  element={
                    <NoIndexWrapper>
                      <NormalPostGenerator />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/photos-post"
                  element={
                    <NoIndexWrapper>
                      <PhotosPostGenerator />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/website-post"
                  element={
                    <NoIndexWrapper>
                      <WebsitePostGenerator />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/classified-ad"
                  element={
                    <NoIndexWrapper>
                      <ClassifiedAdGenerator />
                    </NoIndexWrapper>
                  }
                />
                {/* Khi đã login mà URL vẫn là /login (do FB redirect flow), redirect về /auto-post */}
                <Route
                  path="/login"
                  element={<Navigate to="/auto-post" replace />}
                />
                <Route
                  path="/auto-post"
                  element={
                    <NoIndexWrapper>
                      <AutoPostGenerator />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/manual-post"
                  element={
                    <NoIndexWrapper>
                      <ManualPostGenerator />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/manual-post/configs"
                  element={
                    <NoIndexWrapper>
                      <ManualPostConfigScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/product-auto-post"
                  element={
                    <NoIndexWrapper>
                      <ProductAutoPostGenerator />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/autopost-configs"
                  element={
                    <NoIndexWrapper>
                      <AutoPostConfigScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/lead-dashboard"
                  element={
                    <NoIndexWrapper>
                      <LeadConversionDashboard />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/facebook-comment-automation"
                  element={
                    <NoIndexWrapper>
                      <FacebookCommentAutomationScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/facebook-comment-automation/configs"
                  element={
                    <NoIndexWrapper>
                      <FacebookCommentConfigListScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <NoIndexWrapper>
                      <NotificationScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <NoIndexWrapper>
                      <ProfileScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/product-create"
                  element={
                    <NoIndexWrapper>
                      <ProductCreateScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <NoIndexWrapper>
                      <ProductsScreen />
                    </NoIndexWrapper>
                  }
                />
                {/* Video Creator Route */}
                <Route
                  path="/video-creator"
                  element={
                    <NoIndexWrapper>
                      <VideoCreatorScreen />
                    </NoIndexWrapper>
                  }
                />
                {/* Route path="/pricing" removed, handled above */}
                <Route
                  path="/add-photo-list"
                  element={
                    <NoIndexWrapper>
                      <AddListImagesScreen />
                    </NoIndexWrapper>
                  }
                />
                <Route
                  path="/product-images-manager"
                  element={
                    <NoIndexWrapper>
                      <ProductImagesManagerScreen />
                    </NoIndexWrapper>
                  }
                />
                {/* Route path="/policy" removed, handled above */}
                {/* Route path="/terms" removed, handled above */}
                {/* News routes removed, handled above */}
                {/* Admin News route removed, handled above */}
                <Route path="/affiliate" element={<AffiliateScreen />} />
                {/* User Guide Routes */}
                <Route path="/guide" element={<GuideLayout />}>
                  <Route
                    index
                    element={<Navigate to="/guide/start-here" replace />}
                  />
                  <Route
                    path="start-here"
                    element={<GuidePage {...startHereGuide} />}
                  />
                  <Route
                    path="auto-post"
                    element={<GuidePage {...autoPostGuide} />}
                  />
                  <Route
                    path="add-list-images"
                    element={<GuidePage {...addListImagesGuide} />}
                  />
                  <Route
                    path="product-auto-post"
                    element={<GuidePage {...productAutoPostGuide} />}
                  />
                  <Route
                    path="manual-post"
                    element={<GuidePage {...manualPostGuide} />}
                  />
                  <Route
                    path="normal-post"
                    element={<GuidePage {...normalPostGuide} />}
                  />
                  <Route
                    path="photos-post"
                    element={<GuidePage {...photosPostGuide} />}
                  />
                  <Route
                    path="website-post"
                    element={<GuidePage {...websitePostGuide} />}
                  />
                  <Route
                    path="classified-ad"
                    element={<GuidePage {...classifiedAdGuide} />}
                  />
                  <Route
                    path="manage-automations"
                    element={<GuidePage {...manageAutomationsGuide} />}
                  />
                  <Route
                    path="facebook-comment-automation"
                    element={<GuidePage {...facebookCommentGuide} />}
                  />
                  <Route
                    path="video-creator"
                    element={<GuidePage {...videoCreatorGuide} />}
                  />
                  <Route
                    path="lead-engine"
                    element={<GuidePage {...leadEngineGuide} />}
                  />
                  <Route
                    path="affiliate"
                    element={<GuidePage {...affiliateGuide} />}
                  />
                </Route>
                {/* Consider moving Admin routes here if they need protection */}
              </Route>

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </div>
          {/* Feedback Form Modal */}
          {showFeedbackForm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000, // Ensure it's above other content
                padding: "20px", // Add padding for smaller screens
                overflowY: "auto", // Allow scrolling if form is tall
              }}
            >
              <FeedbackForm onClose={() => setShowFeedbackForm(false)} />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 justify-center items-center">
          <Routes>
            <Route path="/landing-page" element={<LandingPage1 />} />
            <Route path="/pricing" element={<PricingScreen />} />
            <Route path="/login" element={<LoginFB />} />
            <Route path="/admin-news" element={<AdminNewsGenerator />} />
            <Route
              path="/admin"
              element={<Navigate to="/admin/dashboard" replace />}
            />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route
              path="/admin/users"
              element={<AdminUserManagement setActiveMenu={null} />}
            />
            <Route
              path="/admin/users/:userId"
              element={<AdminUserDetailPage />}
            />
            <Route
              path="/admin/affiliates"
              element={<AdminAffiliateManagement />}
            />
            <Route
              path="/admin/withdrawals"
              element={<AdminWithdrawalsScreen />}
            />
            <Route path="/admin/posts" element={<AdminPostManagement />} />
            <Route
              path="/admin/user-posts"
              element={<AdminUserPostManagement />}
            />
            <Route
              path="/admin/post-history"
              element={<AdminPostHistoryManagement />}
            />
            <Route
              path="/admin/transactions"
              element={<AdminTransactionManagement />}
            />
            <Route path="/admin/errors" element={<AdminErrorManagement />} />
            <Route
              path="/admin/notifications"
              element={<AdminNotificationScreen />}
            />
            <Route path="/admin/news" element={<AdminNewsManagement />} />
            <Route path="/admin/settings" element={<AdminDashboard />} />
            <Route
              path="/admin/auto-settings"
              element={<AdminAutoSettings />}
            />
            <Route path="/" element={<LandingPage1 />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      )}
      {showTrialPopup && (
        <NewUserTrialPopup onClose={() => setShowTrialPopup(false)} />
      )}
    </div>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <MaintenanceProvider>
          <MetaPixelPageTracker />
          <ErrorBoundary>
            <AppContent />
            {/* Global Popup Thông báo nâng cấp hệ thống — đã tắt */}
            {/* <SystemUpgradePopup /> */}
          </ErrorBoundary>
        </MaintenanceProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
